import { useState, type FormEvent } from 'react'
import { useAuth } from './AuthProvider'
import { CRM_EMAIL_DOMAIN } from './repository/crmUserRepository'
import { GoogleMark } from '@/shared/ui/ProviderMarks'
import './SignInScreen.css'

/**
 * Screen 1 of the design: centred card on full-bleed navy.
 *
 * The error banner appears when Firebase Auth succeeded but the email is not on
 * the `skidaCrmUsers` allowlist.
 *
 * ## Google first, email under it
 *
 * Google is the way in for everybody with a `@mountainsnow.ski` account, and it
 * is the only one that also signs you into the product project — so it keeps the
 * top of the card and the visual weight. Email and password, added 17 Sep 2026,
 * is for the accounts that have no Google identity, and it says plainly what it
 * cannot do rather than leaving somebody to work out why half the columns are
 * blank.
 *
 * ## Sign in, or make an account — one form, two buttons
 *
 * Being on the allowlist is permission, not an account; the two have to be
 * created separately and nobody should need an administrator for the second.
 * The fields are identical either way, so the form is one form and the mode
 * only changes which button is the primary one and what the footnote says.
 *
 * Creating an account grants nothing. The allowlist is the gate, and a stranger
 * who signs up lands on the rejection banner having read nothing — see the note
 * in `AuthProvider`.
 *
 * Every wrong-credential failure reads the same — see the note in
 * `AuthProvider`. A screen that distinguished "no such user" from "wrong
 * password" would answer, for anybody who asked, which addresses have accounts
 * in the company's own register.
 */
export function SignInScreen() {
  const {
    signInWithGoogle,
    signInWithEmail,
    createAccount,
    sendPasswordReset,
    resendVerification,
    logOut,
    status,
    rejectedEmail,
    unverifiedEmail,
    error,
  } = useAuth()
  const busy = status === 'loading'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [sentReset, setSentReset] = useState(false)
  const [sentVerify, setSentVerify] = useState(false)
  /** Whether the form is signing in or making an account. */
  const [mode, setMode] = useState<'in' | 'new'>('in')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (busy || !email.trim() || !password) return
    setSentReset(false)
    if (mode === 'new') void createAccount(email, password)
    else void signInWithEmail(email, password)
  }

  const reset = async () => {
    setSentReset(false)
    try {
      await sendPasswordReset(email)
      setSentReset(true)
    } catch {
      // The provider has already put the reason in `error`, where the banner
      // below reads it. Nothing useful to add here.
    }
  }

  /**
   * Signed in, and readable to nothing until the address is confirmed.
   *
   * Its own screen rather than a banner under the form: there is exactly one
   * thing to do here, and leaving the email and password fields in front of
   * somebody invites them to try again, which will land them right back here.
   */
  if (status === 'unverified') {
    return (
      <div className="signin">
        <div className="signin__card">
          <div className="signin__logo" aria-hidden="true">
            <img className="signin__icon" src="/AppIconInternal-512.png" alt="" />
          </div>
          <div className="signin__heading">
            <h1>Confirm your email</h1>
            <p>One step left</p>
          </div>

          <div className="signin__error" role="alert">
            <strong>{unverifiedEmail}</strong> has not been confirmed yet. Open the link in the
            email we sent — it opens in a new tab — then come back here and sign in. Nothing in
            Skida Internal can be read until the address is confirmed.
          </div>

          {sentVerify && (
            <div className="signin__ok" role="status">
              A new link is on its way to <strong>{unverifiedEmail}</strong>. Check spam if it does
              not arrive.
            </div>
          )}

          {error && (
            <div className="signin__error" role="alert">
              {error}
            </div>
          )}

          {/*
            The way out once the link has been followed.

            Confirming happens in another tab — the mail client's — so this one
            is still sitting on "not confirmed yet" when the person comes back,
            with no button that does anything for them. Signing out is what
            returns the form, and the label says where it goes rather than what
            it technically does.
          */}
          <button className="btn btn--primary signin__submit" onClick={() => void logOut()}>
            Go to sign in
          </button>

          <button
            className="signin__link"
            type="button"
            onClick={() => {
              setSentVerify(false)
              void resendVerification().then(
                () => setSentVerify(true),
                () => undefined,
              )
            }}
          >
            Send the link again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="signin">
      <div className="signin__card">
        {/* The app icon, as everywhere else. This carried a "SKIDA / PRO CRM"
            wordmark from before the tool was named Skida Internal and given a
            mark of its own — the first screen anybody sees was the last one
            still saying the old name. */}
        <div className="signin__logo" aria-hidden="true">
          <img className="signin__icon" src="/AppIconInternal-512.png" alt="" />
        </div>

        <div className="signin__heading">
          <h1>Sign in to Skida Internal</h1>
          <p>Sales &amp; marketing team only</p>
        </div>

        {/* Google, and not Apple — 3 Sep 2026. Apple sign-in was offered here
            from the first version of this screen and was never configured in
            Firebase Auth, so the button raised `auth/operation-not-allowed`
            every time somebody trusted it. A door that does not open is worse
            than no door. */}
        <button className="signin__oauth" onClick={signInWithGoogle} disabled={busy}>
          <GoogleMark />
          Continue with Google
        </button>

        <div className="signin__or">
          <span>or</span>
        </div>

        <form className="signin__form" onSubmit={submit}>
          <label className="signin__field">
            <span>Email</span>
            <input
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`you@${CRM_EMAIL_DOMAIN}`}
              disabled={busy}
            />
          </label>

          <label className="signin__field">
            <span>Password</span>
            <input
              className="input"
              type="password"
              // The browser is told which of the two this is, so it offers to
              // fill a saved password rather than to invent one, or the reverse.
              autoComplete={mode === 'new' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </label>

          <button
            className="btn btn--primary signin__submit"
            type="submit"
            disabled={busy || !email.trim() || !password}
          >
            {busy
              ? mode === 'new'
                ? 'Creating…'
                : 'Signing in…'
              : mode === 'new'
                ? 'Create account'
                : 'Sign in'}
          </button>

          <div className="signin__switch">
            <button
              className="signin__link"
              type="button"
              onClick={() => {
                setMode(mode === 'new' ? 'in' : 'new')
                setSentReset(false)
              }}
              disabled={busy}
            >
              {mode === 'new' ? 'I already have an account' : 'Create an account'}
            </button>
            {mode === 'in' && (
              <button
                className="signin__link"
                type="button"
                onClick={() => void reset()}
                disabled={busy}
              >
                Forgot your password?
              </button>
            )}
          </div>
        </form>

        {sentReset && (
          <div className="signin__ok" role="status">
            If <strong>{email.trim()}</strong> has an account <em>with a password</em>, a reset link
            is on its way.
            <br />
            An address set up with Google has no password to reset — use Continue with Google
            instead.
          </div>
        )}

        {rejectedEmail && (
          <div className="signin__error" role="alert">
            <strong>{rejectedEmail}</strong> is not on the list of people with access to this
            system, or its access is switched off. A @{CRM_EMAIL_DOMAIN} address is not enough on
            its own — an administrator adds each person under Internal → Team.
          </div>
        )}

        {error && (
          <div className="signin__error" role="alert">
            {error}
          </div>
        )}

        <p className="signin__footnote">
          {mode === 'new'
            ? 'Use the address you were given access on. We will send you a link to confirm it.'
            : `Sign in with your @${CRM_EMAIL_DOMAIN} address.`}
          <br />
          Other addresses need to be approved by an administrator.
          <br />
          {/* Said here rather than discovered later as a screen full of blanks.
              A password proves who you are to this project and cannot be
              replayed to the product one — see `AuthProvider`. */}
          Signing in with email leaves product data disconnected; use Connect product data in the
          header, or sign in with Google.
        </p>
      </div>
    </div>
  )
}
